/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef, useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {
    Box,
    Flex,
    Skeleton,
    Stack,
    Heading,
    Button,
    SimpleGrid
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {useWishList} from '../../../../hooks/use-wish-list'

// import PageActionPlaceHolder from '@salesforce/retail-react-app/app/components/page-action-placeholder'
// import {HeartIcon} from '@salesforce/retail-react-app/app/components/icons'
import ProductItem from '../../../../components/product-item'
import WishlistPrimaryAction from '../wishlist/partials/wishlist-primary-action'
import WishlistSecondaryButtonGroup from './partials/wishlist-secondary-button-group'

import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'
import {
    emptyWishlistContainer,
    emptyWishlistHeading,
    emptyWishlistText,
    emptyWishlistButton,
    wishlistItemBox,
    carouselStyle
} from './index.style'
import {pxToRem} from '../../../../utills/pxToRem'
import sizes from '../../../../theme/foundations/sizes'
import {addressHeadingStyle} from '../addresses/index.style'
import useSitePreference from '../../../../hooks/use-site-preference'
import {getUrlSearchParam} from '../../../../utills/url'
import RecommendedProducts from '../../../../components/recommended-products'

const numberOfSkeletonItems = 3

const AccountWishlist = () => {
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const location = useLocation()
    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])
    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Wishlist' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [isWishlistItemLoading, setWishlistItemLoading] = useState(false)

    const {data: wishListData, isLoading: isWishListLoading} = useWishList()
    const productIds = wishListData?.customerProductListItems?.map((item) => item.productId)

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true,
                locale: 'default'
            }
        },
        {enabled: productIds?.length > 0}
    )

    // If a product is added to the wishlist many times, it gets collapased into 1 line item
    const wishListItems = wishListData?.customerProductListItems?.reduce((itemsData, item) => {
        const productData = productsData?.data?.find((product) => product.id === item.productId)
        return {...itemsData, [item.productId]: {...item, product: productData}}
    }, {})

    const updateCustomerProductListItem = useShopperCustomersMutation(
        'updateCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const {data: customer} = useCurrentCustomer()
    const {data} = useSitePreference('suggestedForYou')
    const suggestedRecommendation = data?.suggestedForYou

    const handleSecondaryAction = async (itemId, promise) => {
        setWishlistItemLoading(true)
        setSelectedItem(itemId)

        try {
            await promise
            // No need to handle error here, as the inner component will take care of it
        } finally {
            setWishlistItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUnavailableProducts = async (unavailableProductIds) => {
        if (!unavailableProductIds.length) return
        await Promise.all(
            unavailableProductIds.map(async (id) => {
                const item = wishListItems?.find((item) => {
                    return item.productId.toString() === id.toString()
                })
                const parameters = {
                    customerId: customer.customerId,
                    itemId: item?.id,
                    listId: wishListData?.id
                }
                await deleteCustomerProductListItem.mutateAsync({parameters})
            })
        )
    }

    const hasWishlistItems = Object.keys(wishListItems ?? {}).length > 0
    const isPageLoading = hasWishlistItems ? isProductsLoading : isWishListLoading

    return (
        <Stack
            data-testid="account-wishlist-page"
            mt={{base: '0 !important', lg: `${pxToRem(64)} !important`}}
            maxWidth={{base: '100vw'}}
        >
            <Heading sx={addressHeadingStyle} tabIndex="0" ref={headingRef}>
                <FormattedMessage defaultMessage="Wishlist" id="account_wishlist.title.wishlist" />
            </Heading>

            {isPageLoading && (
                <Box data-testid="sf-wishlist-skeleton">
                    {new Array(numberOfSkeletonItems).fill(0).map((i, idx) => (
                        <Box
                            key={idx}
                            p={[4, 6]}
                            my={4}
                            border="1px solid"
                            borderColor="gray.100"
                            borderRadius="base"
                        >
                            <Flex width="full" align="flex-start">
                                <Skeleton boxSize={['88px', 36]} mr={4} />

                                <Stack spacing={2}>
                                    <Skeleton h="20px" w="112px" />
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="140px" />
                                </Stack>
                            </Flex>
                        </Box>
                    ))}
                </Box>
            )}

            {!isPageLoading && !hasWishlistItems && (
                <Box sx={emptyWishlistContainer}>
                    <Heading sx={emptyWishlistHeading}>
                        {formatMessage({
                            defaultMessage: 'No Wishlist Items Yet',
                            id: 'account_wishlist.heading.no_wishlist'
                        })}
                    </Heading>
                    <Box sx={emptyWishlistText}>
                        {formatMessage({
                            defaultMessage:
                                "It doesn't look like you've added anything to your wishlist yet. Once you add items, they'll appear here for easy access.",
                            id: 'account_wishlist.description.no_wishlist'
                        })}
                    </Box>
                    <Button
                        as="button"
                        variant="red"
                        type="button"
                        sx={emptyWishlistButton}
                        onClick={() => navigate('/')}
                    >
                        {formatMessage({
                            defaultMessage: 'Shop Now >>',
                            id: 'account_wishlist.button.shop_now'
                        })}
                    </Button>
                </Box>
            )}

            {!isPageLoading &&
                wishListItems &&
                Object.keys(wishListItems).map((key) => {
                    const item = wishListItems[key]
                    if (!item.product) return null
                    return (
                        <Box key={item.id} sx={wishlistItemBox}>
                            <ProductItem
                                key={item.id}
                                isAccountPage={true}
                                isCart={false}
                                product={{
                                    ...item.product,
                                    quantity: item.quantity,
                                    productUrl: item?.product?.slugUrl
                                        ? new URL(item?.product?.slugUrl).pathname
                                        : ''
                                }}
                                showLoading={
                                    (updateCustomerProductListItem.isLoading ||
                                        deleteCustomerProductListItem.isLoading ||
                                        isWishlistItemLoading) &&
                                    selectedItem === item.productId
                                }
                                primaryAction={<WishlistPrimaryAction />}
                                secondaryActions={
                                    <WishlistSecondaryButtonGroup
                                        productListItemId={item.id}
                                        productName={item.product.name}
                                        focusElementOnRemove={headingRef}
                                        onClick={handleSecondaryAction}
                                    />
                                }
                            />
                        </Box>
                    )
                })}

            {/* <UnavailableProductConfirmationModal
                productItems={wishListData?.customerProductListItems}
                handleUnavailableProducts={handleUnavailableProducts}
            /> */}
            <Box sx={carouselStyle}>
                <RecommendedProducts
                    title={suggestedRecommendation}
                    recommender={'best-sellers-null-results'}
                    previousSearchParams={previousSearchParams}
                    showQuickView={true}
                />
            </Box>
        </Stack>
    )
}

export default AccountWishlist
